import * as angular from 'angular';
//import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    AggregationMode,
    ComponentDefinition,
    Dictionary,
    DigitalOrStringConvertibleValue,
    DigitalSetSimpleDictionary,
    Group,
    IValueViewModel,
    SpotValue,
    TagStringValueDto,
} from '../../models/index';
import { SpotValuesWidget } from './spotValuesWidget.widget';
import { HeadingDefinition } from '../logbookTable/HeadingDefinition';
import { IExportToCsvService, IValueFormattingService } from '../../services/';
import { SpotValuesWidgetConfiguration } from './spotValuesWidget.configuration';
import { guidEmpty, momentFormat, toDictionary, unique, ValueTypes } from '../../common';

interface HeadingBuild {
    headings: HeadingDefinition[][];
    order: string[]
}

interface BodyRow {
    timestamp: string;
    values: SpotWidgetViewModel[];
}

interface SpotWidgetViewModel {
    comment: string;
    group: Group;
    identifier: IValueViewModel;
    timestamp: string;
    tag: IValueViewModel;
    value: string;
}

class SpotValuesWidgetController implements angular.IComponentController {
    headings: HeadingDefinition[];
    viewByDate: {
        headings: HeadingDefinition[][];
        body: BodyRow[];
    };
    values: SpotWidgetViewModel[];

    readonly boundData: SpotValue[];
    readonly currentGroupGuid: string;
    readonly digitalSets: DigitalSetSimpleDictionary;
    readonly groups: Group[];
    readonly item: SpotValuesWidget;
    readonly stringValues: TagStringValueDto[];
    readonly viewModels: Dictionary<IValueViewModel>;

    private _allGroups: Dictionary<Group>;

    static $inject = [
        'exportToCsvService',
        'valueFormattingService'
    ];

    constructor(
        private _exportToCsvService: IExportToCsvService,
        private _valueFormattingService: IValueFormattingService
    ) {
        this.viewByDate = {
            headings: [],
            body: []
        };
    }

    $onInit() {
        if (this.item) {
            this.item.exportToCsv = this.exportToCsv.bind(this);
        }
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['groups']?.currentValue) {
            this._allGroups = toDictionary(this.groups);
        }

        if (this.boundData
            && this.viewByDate?.headings
            && this.viewModels
            && this.digitalSets) {
            this.onChange();
        }
    }

    exportToCsv() {
        const group = this.findGroup();
        const fileName = `SpotValues_${group.name}_${moment(new Date()).format('YYYY-MM-DD')}.csv`;
        let data: string[][] = [];
        if (this.item.configuration.is2DTable) {
            const h = this.viewByDate.headings.map(h => h.map(hh => hh.label));
            const b = this.viewByDate.body.map(h => [moment(h.timestamp).format(momentFormat), ...h.values.map(v => v.value)]);
            data = [...h, ...b];
        } else {
            const h = [this.headings.map(h => h.label)];
            const b = this.values.map(v => this.mapRowToCsv(v));
            data = [...h, ...b];
        }
        this._exportToCsvService.getCsvFromData(data, fileName);
    }

    private mapRowToCsv(spot: SpotWidgetViewModel): string[] {
        const result: string[] = [];
        const cfg = this.item.configuration;

        if (cfg.nameConfiguration.hasGroupName) {
            result.push(spot.group?.uniqueName);
        }
        if (cfg.hasTimeStamp) {
            result.push(moment(spot.timestamp).format(momentFormat));
        }
        if (cfg.nameConfiguration.hasName) {
            result.push(spot.identifier?.name);
        }
        if (cfg.nameConfiguration.hasShortComment) {
            result.push(spot.identifier?.shortComment);
        }
        if (cfg.hasTagName) {
            result.push(spot.tag?.name);
        }
        if (cfg.nameConfiguration.hasTagShortComment) {
            result.push(spot.tag?.shortComment);
        }
        if (cfg.nameConfiguration.hasUnit) {
            result.push(spot.tag?.unitName);
        }
        if (cfg.hasValue) {
            result.push(spot.value);
        }
        if (cfg.hasUserComment) {
            result.push(spot.comment);
        }

        return result;
    }

    private buildHeadingByDate(configuration: SpotValuesWidgetConfiguration): HeadingBuild {
        const headings: HeadingDefinition[][] = [];
        const group = this.findGroup();

        // prepare tags
        const tags = (this.boundData ?? [])
            .map(v => v.tagGuid)
            .filter(unique)
            .map(g => this.viewModels[g])
            .sort((a, b) => a.name < b.name ? -1 : 1);

        const order = tags.map(t => t.guid);
        // create list of identifier with same order
        const identifiers = order.map(guid => this.findIdentifier(guid, group));

        if (configuration.nameConfiguration.hasGroupName) {
            const list = tags.map<HeadingDefinition>(t => ({ key: t.guid, label: group.uniqueName, style: {}, classed: { 'groupname-column': true } }));
            headings.push([{ label: 'Group', style: {}, classed: { 'groupname-column': true } }, ...list]);
        }

        if (configuration.nameConfiguration.hasName) {
            const list = identifiers.map<HeadingDefinition>(i => ({ label: i.name, style: {}, classed: { 'identifiername-column': true } }));
            headings.push([{ label: 'Identifier', style: {}, classed: { 'identifiername-column': true } }, ...list]);
        }

        if (configuration.nameConfiguration.hasShortComment) {
            const list = identifiers.map<HeadingDefinition>(i => ({ label: i.shortComment, style: {}, classed: { 'identifercomment-column': true } }));
            headings.push([{ label: 'Identifier comment', style: {}, classed: { 'identifercomment-column': true } }, ...list]);
        }

        if (configuration.hasTagName) {
            const list = tags.map(d => ({ label: d.name, style: {}, classed: { 'tagname-column': true } }));
            headings.push([{ label: 'Tag name', style: {}, classed: { 'tagname-column': true } }, ...list]);
        }

        if (configuration.nameConfiguration.hasTagShortComment) {
            const list = tags.map<HeadingDefinition>(t => ({ label: t.shortComment, style: {}, classed: { 'tagcomment-column': true } }));
            headings.push([{ label: 'Comment', style: {}, classed: { 'tagcomment-column': true } }, ...list]);
        }

        if (configuration.nameConfiguration.hasUnit) {
            const list = tags.map<HeadingDefinition>(t => ({ label: t.unitName, style: {}, classed: { 'tagunit-column': true } }));
            headings.push([{ label: 'Unit', style: {}, classed: { 'unitname-column': true } }, ...list]);
        }

        // handle sub-headings
        headings.forEach((h, i) => {
            if (i > 0) {
                h.forEach((hh, j) => {
                    if (j > 0) {
                        hh.classed['sub-heading'] = true;
                    }
                });
            } else {
                if (h.length > 0) {
                    h.forEach(hh => hh.style = { width: `${100 / h.length}%` });
                }
            }
        });

        return {
            headings,
            order
        };
    }

    private buildBodyByDate(order: string[]): BodyRow[] {
        const grouped = _.groupBy(this.boundData, v => v.timestamp);
        return _.keys(grouped)
            .sort((a, b) => a > b ? 1 : -1)
            .map(timestamp => {
                // create an index of values by tag to ease selection
                const index = toDictionary(grouped[timestamp].map(v => Object.assign({}, { guid: v.tagGuid }, v)));
                // find the index of the tag name column
                //const column = headings.findIndex(h => _.some(h, v => v.key));
                // create an ordered array of the values sorted according the header column
                const values = /*headings[column]
                    // remove first column to project
                    .slice(1)*/
                    order
                        .map(h => index[h]);
                return {
                    timestamp: timestamp,
                    values: this.buildViewModels(values, timestamp),
                };
            });
    }

    private formatValue(value: number, vm: IValueViewModel): string {
        const convertible: DigitalOrStringConvertibleValue = {
            value: value,
            valueType: ValueTypes.raw.value
        };
        return this._valueFormattingService.formatValue(
            convertible,
            vm,
            this.digitalSets,
            AggregationMode.None,
            2, // this.item.configuration.decimals,
            undefined,
            this.stringValues
        );
    }

    private buildViewModels(values: SpotValue[], timestamp?: string): SpotWidgetViewModel[] {
        const group = this.findGroup();
        return (values ?? []).map(v => {
            const tag = v ? this.viewModels[v.tagGuid] : undefined;
            const identifier = this.findIdentifier(v?.tagGuid, group);

            return {
                comment: v?.comment,
                group,
                identifier,
                tag: v ? tag : null,
                timestamp: timestamp ?? v.timestamp,
                value: v ? this.formatValue(v.value, tag) : '-',
            };
        });
    }

    /**
     * Return the displayed group for series according to widget config and current group from page
     */
    private findGroup(): Group {
        const cfg = this.item.configuration.group;
        return cfg && cfg !== guidEmpty
            ? this._allGroups[cfg]
            : this._allGroups[this.currentGroupGuid];
    }

    /**
     * Return the appropriate Idenfier according AC in #47012
     * @param tag the tag for which to find the identifier
     */
    private findIdentifier(tagGuid: string, currentGroup: Group): IValueViewModel {
        let result: IValueViewModel = undefined;

        // despite complicated AC, it in fact does not depend on the tag
        // but only on the current group selected
        if (currentGroup) {
            let matches = currentGroup.identifierCollection.filter(i => i.tagDefinitionGuid === tagGuid);
            matches = _.sortBy(matches, i => i.sortingOrder);
            if (matches.length > 0) {
                result = this.viewModels[matches[0].guid];
            }
        }

        return result;
    }

    private onChange() {
        const build = this.getHeadings();
        if (this.item.configuration.is2DTable) {
            this.viewByDate = {
                headings: build.headings,
                body: this.buildBodyByDate(build.order)
            };
        } else {
            this.headings = build.headings[0];
            this.values = this.buildViewModels(this.boundData);
        }
    }

    private getHeadings(): HeadingBuild {
        const { configuration } = this.item;
        let result: HeadingBuild = undefined;
        if (!configuration.is2DTable) {
            const headings: HeadingDefinition[] = [];
            const weights: number[] = [];
            if (configuration.nameConfiguration.hasGroupName) {
                headings.push({ label: 'Group', style: {}, classed: { 'groupname-column': true } });
                weights.push(1);
            }
            if (configuration.hasTimeStamp) {
                headings.push({ label: 'TimeStamp', style: {}, classed: { 'timestamp-column': true } });
                weights.push(2);
            }
            if (configuration.nameConfiguration.hasName) {
                headings.push({ label: 'Identifier', style: {}, classed: { 'identifiername-column': true } });
                weights.push(1);
            }
            if (configuration.nameConfiguration.hasShortComment) {
                headings.push({ label: 'Identifier comment', style: {}, classed: { 'identifiercomment-column': true } });
                weights.push(1);
            }
            if (configuration.hasTagName) {
                headings.push({ label: 'Tag name', style: {}, classed: { 'tagname-column': true } });
                weights.push(2);
            }
            if (configuration.nameConfiguration.hasTagShortComment) {
                headings.push({ label: 'Comment', style: {}, classed: { 'tagcomment-column': true } });
                weights.push(1);
            }
            if (configuration.nameConfiguration.hasUnit) {
                headings.push({ label: 'Unit', style: {}, classed: { 'unitname-column': true } });
                weights.push(1);
            }
            if (configuration.hasValue) {
                headings.push({ label: 'Value', style: {}, classed: { 'value-column': true } });
                weights.push(2);
            }
            if (configuration.hasUserComment) {
                headings.push({ label: 'User comment', style: {}, classed: { 'comment-column': true } });
                weights.push(4);
            }
            const sum = _.sum(weights);
            headings.forEach((heading, i) => heading.style.width = weights[i] / sum * 100 + '%');
            result = { headings: [headings], order: [] };
        } else {
            result = this.buildHeadingByDate(configuration);
        }
        return result;
    }
}

export const wtSpotValuesWidget: ComponentDefinition = {
    componentId: 'wtSpotValuesWidget',
    controller: SpotValuesWidgetController,
    template: require('./spotValuesWidget.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '<',
        currentGroupGuid: '<',
        digitalSets: '<',
        groups: '<',
        item: '<',
        stringValues: '<',
        viewModels: '<',
    }
};